"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  number: "01",
  question: "What is the purpose of supervised parenting time?",
  answer: "Supervised parenting time provides a safe, neutral, and structured environment for children to maintain or build relationships with a parent. Our agency operates under strict professional standards and maintained policies, exercising decision-making authority to ensure that contact occurs in a setting where the child's physical and emotional safety is prioritized."
}, {
  number: "02",
  question: "How do I initiate the intake process?",
  answer: "To begin, please visit our Intake Path page to review our requirements. Our intake policy ensures all documentation is collected for specific documentation purposes and maintained according to our retention schedule; these records are available for review as outlined in our agency procedures. We require all relevant court orders before services commence."
}, {
  number: "03",
  question: "Are your services available for high-conflict situations?",
  answer: "Yes. We specialize in providing neutral support for families navigating high-conflict parenting matters using established high-conflict management approaches and rigorous safety protocols. Our staff is trained to maintain firm boundaries and professional neutrality, ensuring that all interactions remain child-centered and focused on the safety of the family unit."
}, {
  number: "04",
  question: "Do you provide reports for the court?",
  answer: "Yes, we provide comprehensive, factual observation reporting for the court. Our documentation and retention policies ensure that all policy-based reporting is objective and neutral, assisting legal professionals and the court in making informed decisions while adhering to strict agency record-keeping standards."
}, {
  number: "05",
  question: "What is the difference between supervised time and monitored exchange?",
  answer: "Supervised parenting time involves a professional remaining present throughout the visit to ensure safety, while a monitored exchange facilitates a calm transfer of the child. Both fall within our defined scope of services and are governed by the agency’s maintained policies and procedures to ensure consistency and safety for all parties."
}, {
  number: "06",
  question: "Are your services trauma-informed?",
  answer: "Absolutely. Our approach is rooted in trauma-informed care, delivered strictly within established agency policies and professional standards. We prioritize the child's emotional state and create a predictable, safe environment to minimize anxiety, ensuring our care remains clinical, neutral, and respectful of the family's experience."
}, {
  number: "07",
  question: "Do you offer transportation services?",
  answer: "Yes, we provide professional transportation for children and vulnerable individuals. This service is governed by specific transportation safety protocols and documentation requirements, ensuring that the child is safely escorted while maintaining the same level of professional oversight as our in-person supervision services."
}, {
  number: "08",
  question: "How do you handle safety concerns during a visit?",
  answer: "Safety is our primary boundary. Our staff is trained to intervene immediately if behavior violates established rules, adhering to strict policy-based practices. We maintain clear boundaries and will terminate a visit if necessary, ensuring all incidents are recorded under our documentation retention and reporting policies for the court."
}];
export default function Questions() {
  const header = useRevealOnScroll<HTMLDivElement>();
  const list = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="3235" id="immediate-questions" className="bg-background py-24 md:py-32 lg:py-[140px] px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-24 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-8">
            <p className="font-mono text-xs md:text-sm uppercase tracking-widest text-foreground/70 mb-6">
              § 01 — FAQ / SERVICES
            </p>
            <h2 className="font-serif font-default text-4xl md:text-5xl lg:text-[56px] leading-[1.05] text-foreground tracking-tight">
              Clear answers for <span className="italic font-light">complex</span> family needs.
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end lg:items-end">
            <Link to="/ContactPage" className="font-mono text-xs uppercase tracking-widest text-foreground hover:text-primary transition-colors duration-300 inline-flex items-center gap-2 border-b border-foreground/30 hover:border-foreground pb-1">
              Need more clarity? Contact us →
            </Link>
          </div>
        </div>

        {}
        <div ref={list.ref} className={`max-w-[880px] mx-auto transition-all duration-700 ease-out delay-150 ${list.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="group border-b border-secondary/40 last:border-b-0 data-[state=open]:border-l-2 data-[state=open]:border-l-primary data-[state=open]:pl-6 transition-all duration-300">
                <AccordionTrigger className="flex items-start justify-between gap-6 py-7 md:py-8 hover:no-underline [&>svg]:hidden text-left">
                  <div className="flex items-start gap-6 md:gap-10 flex-1">
                    <span className="font-mono text-xs md:text-sm text-foreground/50 pt-2 shrink-0 tracking-wider">
                      {faq.number}
                    </span>
                    <span className="font-serif font-default text-lg md:text-[22px] leading-snug text-foreground">
                      {faq.question}
                    </span>
                  </div>
                  <Plus className="size-5 md:size-6 text-primary shrink-0 mt-2 transition-transform duration-400 ease-out group-data-[state=open]:rotate-45" strokeWidth={1} />
                </AccordionTrigger>
                <AccordionContent className="pl-12 md:pl-[72px] pr-10 md:pr-12 pb-8">
                  <p className="text-base text-foreground/75 leading-[1.8]">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </div>
    </section>;
}